/*
 * The visit log — what this visitor's browser did, one event at a time
 * (docs/project/visitor-click-recording-plan.md §3–§4, §6a).
 *
 * ONE general listener set, not hand-placed calls: every click, the keys pressed outside a text
 * box, which boxes were left (never what was in them), scroll depth, hide / show / leave, script
 * errors and failed requests — plus the named milestones the app raises through VISIT_LOG.mark().
 * Queued in memory and sent in small JSON batches to POST /api/visit-log.
 *
 * ⛔ NEVER: typed text, page text, mouse movement, a cookie, anything in localStorage /
 *   sessionStorage. Ids and data attributes only, so the log reads the same in every language.
 * ⛔ NEVER preventDefault, stopPropagation, or a throw into the page. Every handler is wrapped;
 *   a page whose recorder failed must behave exactly as a page with no recorder.
 *   The ONE DOM write is the hand-off (§6a): a same-origin link pressed gets #v=<this view's id>
 *   in its fragment — never sent to any server, read and removed by the next page's copy of this.
 * ⛔ The url is ABSOLUTE and carries no marker (plan §11 traps 1–2): sendBeacon / fetch go round
 *   util.js's $.ajax prefilter, so a relative url 404s on /de/en/app; a marked url would count as
 *   a landing; and $.ajax would answer a 401 by throwing the visitor back to "/".
 *
 * Timing: every event carries m = ms since the page view began (performance.now(), the browser's
 * steady clock) and each batch n = the same clock at send. The server files an event at its own
 * now − (n − m): a wrong client clock cannot misdate anything (the sound.js rule).
 *
 * Loaded first: main.html renders it synchronously right after the pointer probe, so its error
 * listener exists before the ~40 scripts that follow; the other pages load it `defer`.
 */
(function () {
	"use strict";
	if (window.VISIT_LOG || !window.JSON || !window.performance) { return; }

	var URL_ = "/api/visit-log";
	var MAX_SEQ = 5000, MAX_BATCH = 200, MAX_BYTES = 60000, IDLE_MS = 10000, SOON_MS = 1000, GAP_MS = 2000;
	var me = document.currentScript;
	var PAGE = (me && me.getAttribute("data-page")) || "page";
	var id = newId();
	var seq = 0, queue = [], capped = false, errors = 0;
	var factsSent = false, timingSent = false, translatedSent = false;
	var maxScroll = 0, scrollMarks = 0, lastSend = 0, timer = null, timerAt = 0;
	var ui = { card: null, tip: null };   // set by marks: what overlay is up (screen)
	var hand = readHandoff();

	function newId() {
		var a = new Uint8Array(12), s = "", c = window.crypto || window.msCrypto;
		if (c && c.getRandomValues) { c.getRandomValues(a); }
		else { for (var j = 0; j < a.length; j++) { a[j] = Math.floor(Math.random() * 256); } }
		for (var i = 0; i < a.length; i++) { s += (a[i] % 36).toString(36); }
		return s + Date.now().toString(36).slice(-4);
	}

	function now() { return Math.round(performance.now()); }

	function guard(fn) {
		return function (a, b) { try { return fn(a, b); } catch (e) { /* never into the page */ } };
	}

	// ── the hand-off (§6a) ─────────────────────────────────────────────────────────────────────
	// "#v=<id>" (and "&g=<why>" from VISIT_LOG.go) → prev view id; removed from the address bar.
	function readHandoff() {
		try {
			var m = /^#v=([A-Za-z0-9_-]{8,32})(?:&g=([a-z]{1,12}))?$/.exec(location.hash);
			if (!m) { return {}; }
			history.replaceState(history.state, "", location.pathname + location.search);
			return { prev: m[1], why: m[2] };
		} catch (e) { return {}; }
	}

	function withHandoff(href, why) {
		var u = new URL(href, location.href);
		if (u.origin !== location.origin || u.hash
				|| (u.pathname === location.pathname && u.search === location.search)) { return null; }
		u.hash = "v=" + id + (why ? "&g=" + why : "");
		return u.href;
	}

	function tagLink(ev) {
		var a = ev.target && ev.target.closest && ev.target.closest("a[href]");
		if (!a || /^(javascript|mailto|tel):/i.test(a.getAttribute("href"))) { return; }
		var to = withHandoff(a.href);
		if (to) { a.href = to; }
	}

	// ── what was on screen ─────────────────────────────────────────────────────────────────────
	function onScreen() {
		var s = PAGE;
		if (window.PL_VIEW_STATE) {
			var v = window.PL_VIEW_STATE();
			s += "/" + (v.exercise ? "drill" : v.tab);
			if (!v.exercise && ui.card === "finish") { ui.card = null; }
		}
		if (ui.card) { s += "/" + ui.card; }
		if (ui.tip) { s += "/tip:" + ui.tip; }
		return s;
	}

	// ── what was clicked (§3.3): a data-vl label, else the nearest id, else tag.class ─────────
	function describe(el) {
		if (!el || !el.closest) { return null; }
		var label = el.closest("[data-vl]");
		if (label) { return label.getAttribute("data-vl"); }
		var own = el.tagName.toLowerCase();
		var cls = typeof el.className === "string" && el.className.trim().split(/\s+/)[0];
		if (cls) { own += "." + cls; }
		var withId = el.closest("[id]");
		if (withId) { return "#" + withId.id + (withId === el ? "" : " " + own); }
		return own;
	}

	var LIVE = "a,button,input,select,textarea,label,summary,[role],[onclick],[tabindex],[contenteditable]";

	function isField(el) {
		if (!el || !el.tagName) { return false; }
		if (el.isContentEditable || el.tagName === "TEXTAREA") { return true; }
		return el.tagName === "INPUT"
			&& !/^(button|submit|reset|checkbox|radio|range|color|file|image)$/i.test(el.type);
	}

	// ── the queue ──────────────────────────────────────────────────────────────────────────────
	function push(kind, target, d, x, y, soon) {
		if (capped) { return; }
		var e = { q: seq++, m: now(), k: kind };
		if (seq > MAX_SEQ) { capped = true; e.k = "cap"; queue.push(e); send(); return; }
		if (target) { e.t = String(target).slice(0, 200); }
		try { e.s = onScreen(); } catch (err) { e.s = PAGE; }
		if (x != null) { e.x = Math.round(x); e.y = Math.round(y); }
		if (d) { e.d = d; }
		queue.push(e);
		schedule(soon ? SOON_MS : IDLE_MS);
	}

	// Paced: at most one send per GAP_MS outside hide / leave — the penalty box is per ADDRESS
	// across every route (application.yml `visit-log`), so this page must never be refused.
	function schedule(wait) {
		var at = Math.max(Date.now() + wait, lastSend + GAP_MS);
		if (timer && timerAt <= at) { return; }
		clearTimeout(timer);
		timerAt = at;
		timer = setTimeout(guard(send), at - Date.now());
	}

	function facts() {
		var f = { vw: innerWidth, vh: innerHeight, nav: navType(),
			touch: !!(window.matchMedia && matchMedia("(pointer: coarse)").matches),
			lang: navigator.language, x: { dpr: devicePixelRatio, sw: screen.width, sh: screen.height } };
		try { f.tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { /* old engine */ }
		if (window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches) { f.x.rm = true; }
		if (navigator.connection && navigator.connection.effectiveType) { f.x.ct = navigator.connection.effectiveType; }
		if (hand.prev) { f.prev = hand.prev; }
		var ref = document.referrer;
		if (ref) {
			try {
				var r = new URL(ref);
				if (r.origin === location.origin) { f.fp = r.pathname; } else { f.ref = r.origin; }
			} catch (e) { /* unparseable: nothing */ }
		}
		// The marker, read exactly as onboarding.js attributionQuery(): the address first, then
		// <html data-arrival> (an AI assistant's referral the server read).
		var here = new URLSearchParams(location.search);
		var arrival = new URLSearchParams(document.documentElement.getAttribute("data-arrival") || "");
		function get(n) { return here.get(n) || arrival.get(n); }
		if (get("utm_campaign")) { f.cs = get("utm_campaign"); }
		if (get("utm_source")) { f.src = get("utm_source"); }
		["gclid", "gbraid", "wbraid", "msclkid", "rdt_cid"].some(function (n) {
			if (get(n)) { f.cp = n; f.cid = get(n); return true; }
			return false;
		});
		return f;
	}

	function navEntry() {
		var n = performance.getEntriesByType && performance.getEntriesByType("navigation");
		return n && n[0];
	}

	function navType() {
		var n = navEntry();
		if (document.prerendering || (n && n.activationStart > 0)) { return "prerender"; }
		return n ? n.type : null;
	}

	function send() {
		clearTimeout(timer);
		timer = null;
		// The browser translated the page (Chrome marks <html>): handlers can break under it
		// (§11 trap 10), so a strange timeline gets its explanation in the stream.
		var cl = document.documentElement.classList;
		if (!translatedSent && (cl.contains("translated-ltr") || cl.contains("translated-rtl"))) {
			translatedSent = true;
			push("step", "translated", { lang: document.documentElement.lang });
		}
		if (!queue.length && factsSent) { return; }
		var body = { v: id, p: location.pathname, sc: maxScroll };
		if (!factsSent) { body.f = facts(); factsSent = true; }
		var n = navEntry();
		if (!timingSent && n && n.loadEventEnd > 0) {
			body.tm = { ttfb: Math.round(n.responseStart), dcl: Math.round(n.domContentLoadedEventEnd),
				load: Math.round(n.loadEventEnd) };
			timingSent = true;
		}
		var take = Math.min(queue.length, MAX_BATCH), json;
		do {
			body.e = queue.slice(0, take);
			body.n = now();
			json = JSON.stringify(body);
			take = json.length > MAX_BYTES && take > 1 ? Math.ceil(take / 2) : take;
		} while (json.length > MAX_BYTES && body.e.length > 1);
		queue = queue.slice(body.e.length);
		lastSend = Date.now();
		if (!(navigator.sendBeacon && navigator.sendBeacon(URL_, new Blob([json], { type: "application/json" })))) {
			fetch(URL_, { method: "POST", body: json, keepalive: true, credentials: "same-origin",
				headers: { "Content-Type": "application/json" } }).catch(function () { /* best-effort */ });
		}
		if (queue.length) { schedule(GAP_MS); }
	}

	// ── listeners: document, capture phase, passive ────────────────────────────────────────────
	var opt = { capture: true, passive: true };

	document.addEventListener("click", guard(function (ev) {
		var el = ev.target, d = {};
		if (ev.isTrusted === false) { d.prog = 1; }      // the app's own .click() / a harness (§11 trap 4)
		d.pt = ev.pointerType || (d.prog ? "code" : ev.detail === 0 ? "kbd" : "mouse");
		if (el && el.closest) {
			if (!el.closest(LIVE) && getComputedStyle(el).cursor !== "pointer") { d.inert = 1; }
			var row = el.closest("[data-sense-id],[data-term-id]");
			if (row) { d.row = row.getAttribute("data-sense-id") || row.getAttribute("data-term-id"); }
			tagLink(ev);      // a click with no pointerdown before it (keyboard, .click()) still hands off
			var a = el.closest("a[href]");
			if (a && a.href) {
				var u = new URL(a.href, location.href);
				if (u.origin === location.origin) { d.to = u.pathname; } else { d.host = u.hostname; }
			}
		}
		push("click", describe(el), d, ev.clientX, ev.clientY);
	}), opt);

	// The pointer KIND, once per change rather than per press: a "mouse" visitor (facts.touch reads
	// only the primary pointer) can still have a touchscreen, and a finger PANNING a panel makes no
	// click at all. A mouse-only visit writes nothing here. On WINDOW: the tour wall mutes presses at
	// window capture, and this file loads first, so only a window listener of ours runs before it.
	var lastPt = "mouse";
	document.addEventListener("pointerdown", guard(tagLink), opt);
	window.addEventListener("pointerdown", guard(function (ev) {
		if (ev.pointerType && ev.pointerType !== lastPt) {
			lastPt = ev.pointerType;
			mark("ptype", { pt: lastPt });
		}
	}), opt);

	document.addEventListener("keydown", guard(function (ev) {
		if (ev.repeat) { return; }
		var key = ev.key || "";
		var field = isField(ev.target);
		if (field && key !== "Enter" && key !== "Escape") { return; }
		if (key === "Enter") { tagLink(ev); }
		// A printable key outside a box is recorded as "char", never as itself: typing into a page
		// whose box lost focus must not spell out the words one event at a time.
		var name = key.length === 1 ? (ev.ctrlKey || ev.metaKey || ev.altKey ? "mod+" + key : "char") : key;
		push("key", describe(ev.target), { key: name.slice(0, 20), field: field ? 1 : undefined });
	}), opt);

	document.addEventListener("focusout", guard(function (ev) {
		if (isField(ev.target)) {
			var v = ev.target.isContentEditable ? ev.target.textContent : ev.target.value;
			push("input", describe(ev.target), { len: (v || "").length });
		}
	}), opt);

	window.addEventListener("scroll", guard(function () {
		var h = document.documentElement.scrollHeight;
		if (h <= innerHeight) { return; }
		var pct = Math.min(100, Math.round((scrollY + innerHeight) / h * 100));
		if (pct > maxScroll) { maxScroll = pct; }
		var mark = Math.floor(pct / 25);
		if (mark > scrollMarks) { scrollMarks = mark; push("scroll", String(mark * 25)); }
	}), opt);

	// An INNER panel scrolling (the window listener above sees only the page) — recorded only
	// while a tour popup is up, because that is the question it answers: did the reader move the
	// panel under a popup that then went away (the 2026-10-07 blink, docs/project/tour-popup-blink.md).
	// One mark per scroll GESTURE per panel (a pause of ≥1 s starts a new one), on its leading edge;
	// the panel by id/class, its offset in 100 px. ⚠️ A scroll made by CODE (the walk's own
	// bringIntoView) is a scroll event too — pscroll is "the panel moved", not "the reader moved it".
	var panelScrolled = typeof WeakMap === "function" ? new WeakMap() : null;
	document.addEventListener("scroll", guard(function (ev) {
		var el = ev.target;
		if (!ui.tip || !panelScrolled || !el || el === document || !el.tagName) { return; }
		var t = Date.now(), last = panelScrolled.get(el) || 0;
		panelScrolled.set(el, t);
		if (t - last < 1000) { return; }
		mark("pscroll", { el: describe(el), top: Math.round(el.scrollTop / 100) * 100 });
	}), opt);

	var viewed = false;
	function becameVisible() {
		if (!viewed && document.visibilityState === "visible" && !document.prerendering) {
			viewed = true;
			push("view", null, null, null, null, true);
			if (PAGE === "gate") { mark("gate", { why: hand.why || "server" }); }
		}
	}

	document.addEventListener("visibilitychange", guard(function () {
		if (document.visibilityState === "hidden") {
			push("hide");
			send();       // timers freeze in a hidden tab — this is the send that matters
		} else if (viewed) {
			push("show");
		} else {
			becameVisible();
		}
	}));
	document.addEventListener("prerenderingchange", guard(becameVisible));
	window.addEventListener("pagehide", guard(function () { push("leave"); send(); }));
	window.addEventListener("blur", guard(function (ev) {
		if (ev.target === window && document.visibilityState === "visible") { push("blur"); }
	}));
	window.addEventListener("focus", guard(function (ev) {
		if (ev.target === window && viewed) { push("focus"); }
	}));

	function where(src) {
		try {
			var u = new URL(src, location.href);
			return u.origin === location.origin ? u.pathname : u.host + u.pathname;
		} catch (e) { return null; }
	}

	window.addEventListener("error", guard(function (ev) {
		if (++errors > 20) { return; }
		var el = ev.target;
		if (el && el !== window && el.tagName) {           // a script / image / css that failed to load
			push("error", describe(el), { res: where(el.src || el.href) }, null, null, true);
			return;
		}
		push("error", null, { msg: String(ev.message || "").slice(0, 120),
			file: ev.filename ? where(ev.filename) : null, line: ev.lineno, col: ev.colno }, null, null, true);
	}), true);

	window.addEventListener("unhandledrejection", guard(function (ev) {
		if (++errors > 20) { return; }
		var r = ev.reason;
		push("error", "promise", { msg: String(r && r.message || r).slice(0, 120) }, null, null, true);
	}));

	document.addEventListener("DOMContentLoaded", guard(function () {
		if (window.jQuery) {
			jQuery(document).ajaxError(guard(function (ev, xhr, settings) {
				push("ajaxfail", null, { path: where(settings.url), status: xhr.status }, null, null, true);
			}));
		}
	}));

	// ── milestones (§3.4) ──────────────────────────────────────────────────────────────────────
	function mark(name, detail) {
		if (name === "tip") { ui.tip = detail && detail.step; }
		else if (name === "tipclosed") { ui.tip = null; }
		else if (name === "welcome") { ui.card = "welcome"; }
		else if (name === "start") { ui.card = null; }
		else if (name === "drill") { ui.card = null; }
		else if (name === "drilldone") { ui.card = "finish"; }
		else if (name === "quitcard") { ui.card = "quit"; }
		else if (name === "quitstay" || name === "tourend") { if (ui.card === "quit") { ui.card = null; } }
		push("step", name, detail || null, null, null, true);
	}

	// A message box's resource KEY, by reverse lookup in the loaded bundles (this reader's, then
	// English) — never its text. A message built from a template matches no key: a short hash then.
	function keyOf(text) {
		text = String(text == null ? "" : text);
		var bundles = [document["res_" + (window.curr_locale || "")], document.res_];
		for (var b = 0; b < bundles.length; b++) {
			for (var k in bundles[b] || {}) {
				if (bundles[b][k] === text) { return k; }
			}
		}
		for (var i = 0, h = 0; i < text.length; i++) { h = (h * 31 + text.charCodeAt(i)) | 0; }
		return "#" + (h >>> 0).toString(36);
	}

	window.VISIT_LOG = {
		id: id,
		/** A named milestone. Never throws. */
		mark: guard(mark),
		/** showMessage(text) → the "msg" milestone, carrying the key and never the text. */
		msg: guard(function (text) { mark("msg", { key: keyOf(text) }); }),
		/**
		 * An element as a CLICK row names it (data-vl, else #id, else tag.class) — for a caller that
		 * swallows a press before this file's document listener can see it (the tour wall's mute).
		 */
		describe: guard(describe),
		/**
		 * A navigation made by code, carrying this view's id to the next page (§6a). `why` rides
		 * along for the next page's log (the gate page reads it); `replace` = location.replace.
		 */
		go: function (url, why, replace) {
			var to = null;
			try { to = withHandoff(url, why); } catch (e) { /* plain navigation below */ }
			if (replace) { window.location.replace(to || url); } else { window.location.assign(to || url); }
		}
	};

	if (document.prerendering) { return; }
	becameVisible();
})();
